import { create } from "zustand";
import { telemetry } from "@/utilities/telemetry";
import { isAppleWebView } from "@/utilities/display";
import { getBridge } from "@/utilities/bridge";

type Stream = {
  url: string;
  eventId?: string;
  eventUrl?: string;
  muted?: boolean;
  playing?: boolean;
  /** Used for telemetry; rendering reads from live event data via useEvent */
  sport?: string;
  /** Used for telemetry; rendering reads from live event data via useEvent */
  title?: string;
};

type StreamStore = {
  streams: Stream[];
};

type StreamActions = {
  toggleStream: (stream: Stream) => void;
  closeStream: (stream: Stream) => void;
  closeAllStreams: () => void;
  toggleMuteStream: (stream: Stream) => void;
  openStream: (stream: Stream) => void;
};

type StreamState = StreamStore & StreamActions;

const initialState: StreamStore = {
  streams: [],
};

const useStreamStore = create<StreamState>()((set, get) => ({
  ...initialState,
  toggleStream: (stream) =>
    set((state) => {
      if (stream.sport === "racing") {
        const racingIndex = state.streams.findIndex(
          (s) => s.url === stream.url,
        );
        if (racingIndex !== -1) {
          const newStreams = [...state.streams];
          newStreams[racingIndex] = stream;
          return { streams: newStreams };
        }
      }

      const index = state.streams.findIndex(
        (s) => s.eventId === stream.eventId,
      );
      if (index !== -1) {
        const newStreams = [...state.streams];
        newStreams.splice(index, 1);
        return { streams: newStreams };
      }
      return { streams: [stream, ...state.streams] };
    }),
  closeStream: (stream) =>
    set((state) => {
      const index = state.streams.findIndex(
        (s) => s.eventId === stream.eventId,
      );
      if (index !== -1) {
        const newStreams = [...state.streams];
        newStreams.splice(index, 1);
        return { streams: newStreams };
      }
      return state;
    }),
  closeAllStreams: () => set({ streams: [] }),
  toggleMuteStream: (stream) =>
    set((state) => {
      const index = state.streams.findIndex(
        (s) => s.eventId === stream.eventId,
      );
      if (index !== -1) {
        const newStreams = [...state.streams];
        newStreams[index] = {
          ...newStreams[index],
          muted: !newStreams[index].muted,
        };
        return { streams: newStreams };
      }
      return state;
    }),
  openStream: (stream) => {
    telemetry.streamRequested({
      eventId: stream.eventId,
      url: stream.url,
      title: stream.title,
      sport: stream.sport,
    });
    if (isAppleWebView()) {
      getBridge().send.playStream({
        eventId: stream.eventId ?? "",
        url: stream.url,
      });
    } else {
      get().toggleStream(stream);
    }
  },
}));

export { useStreamStore };
export type { Stream, StreamStore, StreamState };
